import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../../../api/client';
import Logo from '../../../../assets/AptiviewLogo.png';
import SideImage from '../../../../assets/LeftSection.jpeg';
import { FiCheckCircle, FiXCircle, FiLoader, FiInfo } from 'react-icons/fi';

export default function UserActivation() {
    const { userId, token } = useParams();
    const [activationStatus, setActivationStatus] = useState('loading'); // 'loading', 'success', 'error', 'info'
    const [message, setMessage] = useState('');

    useEffect(() => {
        activateUser();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, token]);

    const activateUser = async () => {
        if (!userId || !token) {
            setActivationStatus('error');
            setMessage('Invalid activation link. Please check your email for the correct activation link.');
            return;
        }
        
        try {
            const response = await api.get(`/activate/${userId}/${token}/`);
            
            // Check if the response indicates success
            if (response.status === 200) {
                // Prioritize backend message over default message
                const backendMessage = response.data?.message || response.data?.detail;
                
                if (backendMessage) {
                    // Check if it's already activated message
                    if (backendMessage.toLowerCase().includes('already activated')) {
                        setActivationStatus('info');
                        setMessage(backendMessage);
                    } else {
                        setActivationStatus('success');
                        setMessage(backendMessage);
                    }
                } else {
                    setActivationStatus('success');
                    setMessage('User activated successfully! You can now login to your account.');
                }
            } else {
                setActivationStatus('error');
                setMessage('Account activation failed. Please try again or contact support.');
            }
        } catch (err) {
            console.error('Activation error:', err);
            setActivationStatus('error');
            
            // PRIORITIZE backend response messages
            let errorMessage = '';
            
            // First priority: Backend message/detail from response data
            if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            } else if (err.response?.data?.detail) {
                errorMessage = err.response.data.detail;
            } else if (err.response?.data?.error) {
                errorMessage = err.response.data.error;
            } else {
                // Fallback to status-based messages only if no backend message
                switch (err.response?.status) {
                    case 400:
                        errorMessage = 'Invalid or expired activation link. Please request a new activation email.';
                        break;
                    case 404:
                        errorMessage = 'Activation link not found. Please check your email for the correct link.';
                        break;
                    case 410:
                        errorMessage = 'This activation link has expired. Please request a new activation email.';
                        break;
                    default:
                        errorMessage = 'Account activation failed. Please try again later or contact support.';
                }
            }
            
            setMessage(errorMessage);
        } finally {
        }
    };

    const getStatusIcon = () => {
        switch (activationStatus) {
            case 'loading':
                return <FiLoader size={64} className="text-primary mb-3 animate-spin" />;
            case 'success':
                return <FiCheckCircle size={64} className="text-success mb-3" />;
            case 'info':
                return <FiInfo size={64} className="text-info mb-3" />;
            case 'error':
                return <FiXCircle size={64} className="text-danger mb-3" />;
            default:
                return <FiLoader size={64} className="text-primary mb-3" />;
        }
    };

    const getStatusTitle = () => {
        switch (activationStatus) {
            case 'loading':
                return 'Activating Your Account...';
            case 'success':
                return 'Account Activated Successfully!';
            case 'info':
                // return 'Account Status';
                return '';
            case 'error':
                return 'Activation Failed';
            default:
                return 'Processing...';
        }
    };

    const getStatusColor = () => {
        switch (activationStatus) {
            case 'success':
                return 'text-success';
            case 'info':
                return 'text-info';
            case 'error':
                return 'text-danger';
            default:
                return 'text-primary';
        }
    };

    return (
        <div className="container-fluid min-vh-100 d-flex p-0">
            <div className="row flex-fill w-100 m-0">
                <div className="col-md-8 d-none d-md-block p-0">
                    <img
                        src={SideImage}
                        alt="Activation Visual"
                        className="img-fluid h-100 w-100"
                        style={{ objectFit: 'cover' }}
                    />
                </div>
                <div className="col-md-4 d-flex align-items-center justify-content-center p-4">
                    <div className="w-100 p-4 text-center" style={{ maxWidth: '400px' }}>
                        {/* Logo */}
                        <div className="text-center mb-4">
                            <img
                                src={Logo}
                                alt="Logo"
                                className="mb-3 rounded"
                                style={{ transform: 'scale(0.7)' }}
                            />
                        </div>

                        {/* Status Icon */}
                        <div className="text-center mb-4">
                            <div className="d-flex justify-content-center">
                                {getStatusIcon()}
                            </div>
                            <h2 className={`fw-bold ${getStatusColor()}`}>
                                {getStatusTitle()}
                            </h2>
                        </div>

                        {/* Status Message */}
                        <div className="mb-4">
                            {activationStatus === 'loading' && (
                                <div className="alert alert-info">
                                    <p className="mb-0">
                                        Please wait while we activate your account...
                                    </p>
                                </div>
                            )}

                            {activationStatus === 'success' && (
                                <div className="alert alert-success">
                                    <p className="mb-2">
                                        <strong>Congratulations!</strong>
                                    </p>
                                    <p className="mb-0">
                                        {message}
                                    </p>
                                </div>
                            )}

                            {activationStatus === 'info' && (
                                <div className="alert alert-info">
                                    <p className="mb-2">
                                        {/* <strong>Information</strong> */}
                                    </p>
                                    <p className="mb-0">
                                        {message}
                                    </p>
                                </div>
                            )}

                            {activationStatus === 'error' && (
                                <div className="alert alert-danger">
                                    <p className="mb-2">
                                        <strong>Activation Error</strong>
                                    </p>
                                    <p className="mb-0">
                                        {message}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Action Buttons */}
                        {activationStatus !== 'loading' && (
                            <div className="d-grid gap-2">
                                {(activationStatus === 'success' || activationStatus === 'info') && (
                                    <>
                                        <Link 
                                            to="/login" 
                                            className="btn btn-primary btn-lg"
                                        >
                                            Go to Login
                                        </Link>
                                        <hr className="my-3" />
                                    </>
                                )}

                                {activationStatus === 'error' && (
                                    <>
                                        <button 
                                            className="btn btn-outline-primary"
                                            onClick={activateUser}
                                        >
                                            Try Again
                                        </button>
                                        <Link 
                                            to="/login" 
                                            className="btn btn-outline-secondary"
                                        >
                                            Back to Login
                                        </Link>
                                        <hr className="my-3" />
                                    </>
                                )}

                                {/* Close Window Button */}
                                {/* <button 
                                    className="btn btn-light d-flex align-items-center justify-content-center gap-2"
                                    onClick={handleCloseWindow}
                                >
                                    <FiX size={16} />
                                    Close This Page
                                </button> */}
                            </div>
                        )}

                        {/* Additional Info */}
                        {activationStatus === 'error' && (
                            <div className="mt-4">
                                <small className="text-muted">
                                    If you continue to experience issues, please contact our support team.
                                </small>
                            </div>
                        )}

                        {(activationStatus === 'success' || activationStatus === 'info') && (
                            <div className="mt-4">
                                <small className="text-muted">
                                    {activationStatus === 'success' 
                                        ? 'Your account is now fully activated. You can proceed to login.'
                                        : 'You can proceed to login with your existing credentials.'
                                    }
                                </small>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}